// Google Sheets content extraction using public export URLs

import type { SheetsExtractionResult } from './types';
import { GoogleExtractionError } from './errors';

/**
 * Default timeout for Google Sheets export requests (10 seconds)
 */
const SHEETS_EXPORT_TIMEOUT = 10000;

/**
 * Builds the Google Sheets CSV export URL
 *
 * @param docId - The Google Sheets document ID
 * @param gid - Optional sheet ID (gid) for multi-sheet documents
 * @returns The export URL for CSV format
 */
export function buildSheetsExportUrl(docId: string, gid?: string): string {
  const baseUrl = `https://docs.google.com/spreadsheets/d/${docId}/export?format=csv`;
  return gid ? `${baseUrl}&gid=${gid}` : baseUrl;
}

/**
 * Extracts content from a public Google Sheets document
 *
 * Uses Google's public export URL which works for documents
 * with "Anyone with the link" sharing enabled.
 *
 * No API key required for public documents.
 *
 * @param docId - The Google Sheets document ID
 * @returns Extraction result with CSV content
 * @throws GoogleExtractionError for access, network, or extraction errors
 */
export async function extractGoogleSheets(docId: string): Promise<SheetsExtractionResult> {
  const exportUrl = buildSheetsExportUrl(docId);

  try {
    const response = await fetch(exportUrl, {
      headers: {
        Accept: 'text/csv',
      },
      signal: AbortSignal.timeout(SHEETS_EXPORT_TIMEOUT),
    });

    // Handle HTTP errors
    if (!response.ok) {
      if (response.status === 403 || response.status === 401) {
        throw new GoogleExtractionError('PRIVATE_DOCUMENT');
      }

      if (response.status === 404) {
        throw new GoogleExtractionError('NOT_FOUND');
      }

      if (response.status === 429) {
        throw new GoogleExtractionError('RATE_LIMITED');
      }

      throw new GoogleExtractionError('EXTRACTION_FAILED');
    }

    const csvContent = await response.text();

    // For now, just extract first sheet
    // Multi-sheet support can be added later by parsing HTML to get sheet list
    return {
      title: 'Google Sheets Document',
      content: csvContent,
      characterCount: csvContent.length,
      sheetCount: 1,
    };
  } catch (error) {
    // Re-throw GoogleExtractionError as-is
    if (error instanceof GoogleExtractionError) {
      throw error;
    }

    // Handle timeout/abort errors
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new GoogleExtractionError('TIMEOUT');
    }

    // Handle other errors
    throw new GoogleExtractionError('EXTRACTION_FAILED');
  }
}
